import { useState } from "react"
import { Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { setFromEvent } from "lib/setFromEvent"
import { hasDemand } from "lib/authorization/has-demand"
import { resetScimCircuitBreaker } from "routes/settings/plugins/scim/controller/scim-circuit-breaker"
import {
    getScimCircuitBreakerDetail,
    setScimBreakerOverride,
} from "routes/settings/plugins/scim/controller/scim-breaker-override"

export function CircuitBreakerSection() {
    const { client } = useBoundContext()
    const clientId = client?.id
    const isAdmin = hasDemand("admin")
    const [maxOverride, setMaxOverride] = useState(undefined)
    const status = getScimCircuitBreakerDetail.useResults({ clientId })

    if (!status) return null

    const trippedAtLabel = formatTrippedAt(status.trippedAt)
    const overrideValue = maxOverride ?? status.breakerMaxDeprovisions ?? ""

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
                Bulk-deprovision circuit breaker
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                If SCIM attempts to deactivate more than {status.thresholdAbsolute} users, or more than{" "}
                {status.thresholdPercent}% of active users (only enforced when there are at least{" "}
                {status.minActiveForPercent} active users), within {status.windowMinutes} minutes, the breaker trips and
                further deprovisions are rejected with a 429 until an admin re-arms.
            </Typography>

            <If value={status.breakerDisabled}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The breaker is disabled for this organisation — bulk deprovisions are never blocked.
                </Alert>
            </If>

            {/* The armed/tripped pair is suppressed entirely while the breaker is disabled: a
                disabled breaker never blocks, so "Breaker armed ... well below threshold" beside
                the disabled warning told the admin two contradictory things at once. */}
            <If value={!status.breakerDisabled}>
                <If value={status.tripped}>
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={reset}>
                                Re-arm
                            </Button>
                        }
                    >
                        <Stack spacing={0.5}>
                            <Typography variant="body2">
                                <strong>Breaker tripped.</strong> {status.deprovisionsInWindow} deprovisions in the last{" "}
                                {status.windowMinutes} minutes.
                            </Typography>
                            <If value={!!status.reason}>
                                <Typography variant="body2">{status.reason}</Typography>
                            </If>
                            <Typography variant="caption" color="text.secondary">
                                {trippedAtLabel}Review Sync history below before re-arming.
                            </Typography>
                        </Stack>
                    </Alert>
                    <Else>
                        <Alert severity="success">
                            Breaker armed. {status.deprovisionsInWindow} deprovisions in the last {status.windowMinutes}{" "}
                            minutes (well below threshold).
                        </Alert>
                    </Else>
                </If>
            </If>

            <If value={isAdmin}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <TextField
                        size="small"
                        type="number"
                        label="Max deprovisions override"
                        helperText="Blank uses the default threshold"
                        value={overrideValue}
                        onChange={setFromEvent(setMaxOverride)}
                        data-cy="scim-breaker-override-field"
                    />
                    <Button size="small" variant="outlined" onClick={saveOverride} data-cy="scim-breaker-override-save">
                        Save override
                    </Button>
                </Stack>
                {/* `breakerDisabled` was stored, mutable and reported, but nothing in the UI could
                    SET it — the section only rendered a warning once it was already true, so the
                    only way to turn a tenant's breaker off was a hand-written GraphQL call. The
                    capability exists for small tenants that cannot be cleaned up under the default
                    threshold; it needs to be reachable by the admins it exists for. */}
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                        <Switch
                            checked={!!status.breakerDisabled}
                            onChange={toggleDisabled}
                            data-cy="scim-breaker-disabled-toggle"
                        />
                    }
                    label="Disable the breaker for this organisation"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Bulk deprovisions are never blocked while this is on. Deprovisions are still recorded and reported,
                    so Sync history and the estate view stay accurate.
                </Typography>
            </If>
        </Box>
    )

    async function reset() {
        try {
            await resetScimCircuitBreaker({ clientId })
            invalidate("getScimCircuitBreaker")
            successSnackbar("Circuit breaker re-armed")
        } catch (error) {
            errorSnackbar(error?.message || "Re-arm failed")
        }
    }

    async function toggleDisabled(_, next) {
        try {
            await setScimBreakerOverride({ clientId, breakerDisabled: next })
            invalidate("getScimCircuitBreaker")
            successSnackbar(next ? "Breaker disabled for this organisation" : "Breaker re-enabled")
        } catch (error) {
            errorSnackbar(error?.message || "Could not change the breaker setting")
        }
    }

    async function saveOverride() {
        const trimmed = `${overrideValue}`.trim()
        // Validate the TEXT before parsing. Number.parseInt truncates, so "1.5" became 1 and then
        // sailed through an Number.isInteger check — the validation could never reject a
        // non-integer, and the admin was silently given a threshold they did not type.
        if (trimmed !== "" && !/^\d+$/.test(trimmed)) {
            errorSnackbar("Override must be a positive whole number")
            return
        }
        const parsed = trimmed === "" ? null : Number.parseInt(trimmed, 10)
        if (parsed !== null && parsed < 1) {
            errorSnackbar("Override must be a positive whole number")
            return
        }
        try {
            await setScimBreakerOverride({ clientId, breakerMaxDeprovisions: parsed })
            invalidate("getScimCircuitBreaker")
            setMaxOverride(undefined)
            // Say which of the two things actually happened. The field is type="number", so a
            // non-numeric entry never reaches it and Save sees an empty string — reporting
            // "saved" there told an admin who typed rubbish that their value had been stored,
            // while what really happened was the tenant's override being removed.
            successSnackbar(parsed === null ? "Breaker override cleared" : "Breaker override saved")
        } catch (error) {
            errorSnackbar(error?.message || "Save failed")
        }
    }

    function formatTrippedAt(trippedAt) {
        const ms = Number(trippedAt)
        if (!ms || Number.isNaN(ms)) return ""
        return `Tripped at ${new Date(ms).toLocaleString()}. `
    }
}
